// src/api.ts
import axios, { AxiosRequestConfig } from "axios";
import FileSaver from "file-saver";
import { localStorageBase64 } from "../Atom/localStorageBase64";

const BASE = (process.env.REACT_APP_API || "/api").replace(/\/+$/, ""); // 끝 슬래시 제거

// 공통 인스턴스 (세션 안 쓰니 withCredentials=false)
const http = axios.create({
  baseURL: BASE, // 예: https://www.ocollorconsulting.com:8060/api
  timeout: 15000,
  withCredentials: false,
  headers: { Accept: "*/*" },
});

// 토큰 자동 첨부
http.interceptors.request.use((config) => {
  const token = localStorageBase64().getItem("accessToken");
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as any).authorization = `Bearer ${token}`;
  }
  return config;
});

// ---- helpers ----
const getJson = async <T = any,>(
  path: string | number,
  params?: Record<string, any>,
  cfg?: AxiosRequestConfig
) => {
  const { data, status } = await http.get(String(path), {
    params,
    ...(cfg || {}),
  });
  if (status !== 200) return undefined;
  return (data?.data ?? data) as T;
};

const getFile = async (
  path: string | number,
  filename: string,
  params?: Record<string, any>
) => {
  const res = await http.get(String(path), { params, responseType: "blob" });
  const type = res.headers["content-type"] || "application/octet-stream";
  FileSaver.saveAs(new Blob([res.data], { type }), filename);
};

const postJson = async <T = any,>(
  path: string | number,
  body: any,
  cfg?: AxiosRequestConfig
) => {
  const { data } = await http.post(String(path), body, {
    headers: { "Content-Type": "application/json;charset=UTF-8" },
    ...(cfg || {}),
  });
  return (data?.data ?? data) as T;
};

const postFile = async <T = any,>(
  path: string | number,
  formData: FormData,
  cfg?: AxiosRequestConfig
) => {
  // Content-Type 지정하지 말 것! (브라우저가 boundary 포함해 자동 설정)
  const { data } = await http.post(String(path), formData, { ...(cfg || {}) });
  return (data?.data ?? data) as T;
};

const putJson = async <T = any,>(
  path: string | number,
  body: any,
  cfg?: AxiosRequestConfig
) => {
  const { data } = await http.put(String(path), body, {
    headers: { "Content-Type": "application/json;charset=UTF-8" },
    ...(cfg || {}),
  });
  return (data?.data ?? data) as T;
};

const putFile = async <T = any,>(
  path: string | number,
  formData: FormData,
  cfg?: AxiosRequestConfig
) => {
  const { data } = await http.put(String(path), formData, { ...(cfg || {}) });
  return (data?.data ?? data) as T;
};

const delJson = async <T = any,>(
  path: string | number,
  cfg?: AxiosRequestConfig
) => {
  const { data } = await http.delete(String(path), { ...(cfg || {}) });
  return (data?.data ?? data) as T;
};

// ---- public api ----
const Api = {
  get: getJson,
  getfile: getFile,
  post: postJson,
  postfile: postFile,
  put: putJson,
  putfile: putFile,
  delete: delJson,
};

export default Api;
